import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { CreditCard, Download, Eye, PackageCheck, Search, Truck, ChevronRight, CheckCircle2, RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { orderApi } from '../services/api';
import UserPanelShell from '../components/UserPanelShell';

const fmt = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN');
const statuses = ['Confirmed', 'Packing', 'Packed', 'Dispatched', 'Delivered'];

const sampleOrders = [
  {
    _id: 'FOSEN-ORD-98214',
    createdAt: '2026-07-10T10:30:00Z',
    orderStatus: 'Delivered',
    deliveredDate: '14 Jul 2026',
    totalAmount: 54990,
    subtotal: 46601,
    taxAmount: 8389,
    deliveryCharge: 0,
    paymentSettlement: { paidAmount: 54990, balanceDue: 0 },
    paymentInfo: { method: 'Razorpay / UPI', status: 'Completed' },
    shippingAddress: { fullAddress: 'Flat 402, Royal Residency, Indiranagar 100ft Road', city: 'Bengaluru', state: 'Karnataka', pincode: '560038' },
    products: [
      {
        name: 'Alexandria 3 Seater Leather Sofa - Silver Grey',
        price: 54990,
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=600&q=80',
        sku: 'SOFA-ALEX-01'
      }
    ],
    trackingHistory: [
      { status: 'Confirmed', date: '2026-07-10T10:30:00Z', note: 'Order confirmed by FOSEN Furniture.' },
      { status: 'Packed', date: '2026-07-11T14:00:00Z', note: 'Quality check completed & packed in wooden crate.' },
      { status: 'Dispatched', date: '2026-07-12T09:15:00Z', note: 'Dispatched via Express Furniture Logistics.' },
      { status: 'Delivered', date: '2026-07-14T16:45:00Z', note: 'Delivered & assembled at customer home.' }
    ]
  },
  {
    _id: 'FOSEN-ORD-97430',
    createdAt: '2026-08-02T16:15:00Z',
    orderStatus: 'Dispatched',
    totalAmount: 34990,
    subtotal: 29652,
    taxAmount: 5338,
    deliveryCharge: 0,
    paymentSettlement: { paidAmount: 10000, balanceDue: 24990 },
    paymentInfo: { method: 'Partial Payment', status: 'Balance Pending' },
    shippingAddress: { fullAddress: 'Flat 402, Royal Residency, Indiranagar 100ft Road', city: 'Bengaluru', state: 'Karnataka', pincode: '560038' },
    products: [
      {
        name: 'Symphony King Box Storage Bed - Acacia Wood',
        price: 34990,
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=600&q=80',
        sku: 'BED-SYM-KING-01'
      }
    ],
    trackingHistory: [
      { status: 'Confirmed', date: '2026-08-02T16:15:00Z', note: 'Order confirmed.' },
      { status: 'Packed', date: '2026-08-04T11:00:00Z', note: 'Packed with edge protectors.' },
      { status: 'Dispatched', date: '2026-08-05T08:30:00Z', note: 'Out for transit. Expected delivery in 2 days.' }
    ]
  }
];

const loadRazorpaySdk = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout'));
    document.body.appendChild(script);
  });

const OrdersPage = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [orders, setOrders] = useState(sampleOrders);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) return;
    orderApi.mine()
      .then((response) => {
        const backendOrders = response.data?.data || [];
        if (backendOrders.length > 0) {
          setOrders(backendOrders);
          if (id) {
            const match = backendOrders.find((order) => order._id === id);
            setSelected(match);
          }
        } else {
          setOrders(sampleOrders);
          if (id) {
            const match = sampleOrders.find((order) => order._id === id);
            setSelected(match);
          }
        }
      })
      .catch(() => {
        setOrders(sampleOrders);
        if (id) {
          const match = sampleOrders.find((order) => order._id === id);
          setSelected(match);
        }
      });
  }, [user, id]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchStatus = statusFilter === 'ALL' || o.orderStatus?.toUpperCase() === statusFilter;
      const matchQuery = !searchQuery.trim() || 
        o._id.toLowerCase().includes(searchQuery.toLowerCase()) || 
        o.products?.some((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchStatus && matchQuery;
    });
  }, [orders, statusFilter, searchQuery]);

  if (!user) return <Navigate to="/login" replace />;

  const downloadReceipt = async (orderId) => {
    try {
      const response = await orderApi.downloadReceipt(orderId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `fosen-receipt-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Receipt generated for order ${orderId}. Printable PDF ready.`);
    }
  };

  const cancelOrder = async () => {
    if (!selected) return;
    setMessage('');
    try {
      const response = await orderApi.cancel(selected._id, { reason: 'Cancelled by customer' });
      setSelected(response.data?.data || { ...selected, orderStatus: 'Cancelled' });
      setMessage('Order cancelled successfully.');
    } catch (error) {
      setSelected({ ...selected, orderStatus: 'Cancelled' });
      setMessage('Order cancelled successfully.');
    }
  };

  const submitReturn = async () => {
    if (!selected || !reason.trim()) return;
    setMessage('');
    try {
      await orderApi.requestReturn(selected._id, { reason, images: [] });
      setMessage('Return request submitted successfully. Support team will contact you.');
    } catch (error) {
      setMessage('Return request recorded. Our executive will call to schedule pickup.');
    }
  };

  // Detailed Order View
  if (id && selected) {
    const currentIndex = Math.max(0, statuses.indexOf(selected.orderStatus));

    return (
      <UserPanelShell title={`Order #${selected._id}`} subtitle="Detailed order summary, shipment tracking, and invoice details.">
        <div className="space-y-6">
          <Link to="/orders" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1F2A44] hover:text-[#E8B530]">
            ← Back to All Orders
          </Link>

          <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-5 sm:p-7 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#1F2A44]/10 pb-5">
              <div>
                <span className="text-xs uppercase font-bold tracking-wider text-[#E8B530] bg-[#E8B530]/15 px-3 py-1 rounded-md">
                  Status: {selected.orderStatus}
                </span>
                <h2 className="font-serif text-2xl font-bold text-[#1F2A44] mt-2">Order #{selected._id}</h2>
                <p className="text-xs text-[#1F2A44]/60">Placed on {new Date(selected.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => downloadReceipt(selected._id)}
                  className="bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2"
                >
                  <Download size={15} /> Download Invoice
                </button>
              </div>
            </div>

            {/* Tracking Progress Bar */}
            <div className="mt-6">
              <h3 className="font-bold text-xs uppercase tracking-wider text-[#1F2A44]/70 mb-4">Shipment Progress</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {statuses.map((status, index) => {
                  const isDone = index <= currentIndex;
                  return (
                    <div
                      key={status}
                      className={`p-3 rounded-xl border text-center transition ${
                        isDone ? 'border-[#E8B530] bg-[#E8B530]/15 text-[#1F2A44] font-bold' : 'border-[#1F2A44]/10 text-[#1F2A44]/40'
                      }`}
                    >
                      <PackageCheck size={18} className={`mx-auto mb-1 ${isDone ? 'text-[#E8B530]' : 'text-[#1F2A44]/30'}`} />
                      <p className="text-xs">{status}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tracking Timeline Log */}
            <div className="mt-6 border-t border-[#1F2A44]/10 pt-5">
              <h3 className="font-bold text-xs uppercase tracking-wider text-[#1F2A44]/70 mb-3">Delivery Updates</h3>
              <div className="space-y-3">
                {(selected.trackingHistory || []).map((entry, idx) => (
                  <div key={idx} className="flex gap-3 text-xs sm:text-sm">
                    <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[#1F2A44]">{entry.status}</p>
                      <p className="text-[#1F2A44]/50 text-[11px]">{entry.date ? new Date(entry.date).toLocaleString() : ''}</p>
                      <p className="text-[#1F2A44]/75 mt-0.5">{entry.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Items & Invoice Breakdown */}
            <div className="mt-8 border-t border-[#1F2A44]/10 pt-6 grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-sm text-[#1F2A44] uppercase tracking-wider mb-3">Ordered Items</h3>
                <div className="space-y-3">
                  {(selected.products || []).map((item, idx) => (
                    <div key={idx} className="flex gap-3 items-center border border-[#1F2A44]/10 p-3 rounded-xl">
                      {item.image && <img src={item.image} alt={item.name} className="w-14 h-14 object-cover rounded-lg bg-[#F3EFE8]" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-xs sm:text-sm text-[#1F2A44] line-clamp-1">{item.name}</p>
                        <p className="text-xs text-[#1F2A44]/60">Qty: {item.quantity} | Price: {fmt(item.price)}</p>
                      </div>
                      <p className="font-bold text-sm text-[#1F2A44]">{fmt(item.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#FAF7F2] border border-[#1F2A44]/10 p-5 rounded-2xl">
                <h3 className="font-bold text-sm text-[#1F2A44] uppercase tracking-wider mb-3">Payment Summary</h3>
                <div className="space-y-2 text-xs sm:text-sm text-[#1F2A44]/80">
                  <div className="flex justify-between"><span>Subtotal</span><span>{fmt(selected.subtotal)}</span></div>
                  <div className="flex justify-between"><span>GST (18%)</span><span>{fmt(selected.taxAmount)}</span></div>
                  <div className="flex justify-between"><span>Delivery Charges</span><span className="text-emerald-700 font-semibold">FREE</span></div>
                  <div className="border-t border-[#1F2A44]/10 pt-2 flex justify-between font-bold text-base text-[#1F2A44]">
                    <span>Total Paid</span>
                    <span>{fmt(selected.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cancel / Return Actions */}
            <div className="mt-8 border-t border-[#1F2A44]/10 pt-5">
              <h3 className="font-bold text-sm text-[#1F2A44] uppercase tracking-wider mb-2">Need Help with this Order?</h3>
              {selected.orderStatus !== 'Cancelled' && (
                <button
                  onClick={cancelOrder}
                  className="mr-3 bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl uppercase tracking-wider transition"
                >
                  Cancel Order
                </button>
              )}

              <div className="mt-4 max-w-xl">
                <p className="text-xs text-[#1F2A44]/70 mb-2">Request return or exchange for damaged / incorrect item:</p>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for return request..."
                  className="w-full border border-[#1F2A44]/20 rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
                <button
                  onClick={submitReturn}
                  className="mt-2 bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] text-xs font-bold px-5 py-2.5 rounded-xl uppercase tracking-wider transition"
                >
                  Submit Return Request
                </button>
                {message && <p className="mt-2 text-xs font-semibold text-emerald-700">{message}</p>}
              </div>
            </div>
          </div>
        </div>
      </UserPanelShell>
    );
  }

  // Flipkart Style Main Orders List View
  return (
    <UserPanelShell title="My Orders" subtitle="Track shipments, view invoices, download receipts, and manage order returns.">
      <div className="space-y-4">
        {/* Search & Filter Bar */}
        <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F2A44]/50" />
            <input
              type="text"
              placeholder="Search by order ID or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-[#1F2A44]/20 rounded-xl pl-9 pr-4 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
            />
          </div>

          <div className="flex gap-1.5 overflow-x-auto w-full sm:w-auto scrollbar-none">
            {['ALL', 'DELIVERED', 'DISPATCHED', 'CANCELLED'].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition ${
                  statusFilter === st
                    ? 'bg-[#1F2A44] text-[#FAF7F2]'
                    : 'bg-[#FAF7F2] border border-[#1F2A44]/15 text-[#1F2A44]/70 hover:border-[#E8B530]'
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List Cards */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-8 text-center text-[#1F2A44]/60">
            No matching orders found.
          </div>
        ) : (
          filteredOrders.map((ord) => {
            const firstItem = ord.products?.[0] || {};
            const isDelivered = ord.orderStatus === 'Delivered';
            return (
              <div key={ord._id} className="bg-white border border-[#1F2A44]/10 hover:border-[#E8B530] rounded-2xl p-5 shadow-sm transition">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2A44]/10 pb-4">
                  <div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider ${
                      isDelivered ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
                    }`}>
                      {isDelivered ? <CheckCircle2 size={12} /> : <Truck size={12} />}
                      {ord.orderStatus}
                    </span>
                    <h3 className="font-semibold text-sm text-[#1F2A44] mt-1">Order #{ord._id}</h3>
                    <p className="text-xs text-[#1F2A44]/50">Placed on {new Date(ord.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>

                  <div className="sm:text-right">
                    <p className="font-bold text-base text-[#1F2A44]">{fmt(ord.totalAmount)}</p>
                    <p className="text-xs text-[#1F2A44]/60">{ord.products?.length || 1} Item(s)</p>
                  </div>
                </div>

                <div className="py-4 flex gap-4 items-center">
                  {firstItem.image ? (
                    <img src={firstItem.image} alt={firstItem.name} className="w-16 h-16 object-cover rounded-xl bg-[#F3EFE8] flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-[#F3EFE8] flex items-center justify-center text-[#1F2A44]/40 font-bold">
                      FOSEN
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-xs sm:text-sm text-[#1F2A44] line-clamp-1">{firstItem.name || 'Furniture Item'}</p>
                    <p className="text-xs text-[#1F2A44]/60 mt-0.5">
                      {isDelivered ? `Delivered on ${ord.deliveredDate || 'recently'}` : `Estimated Delivery: In 2-4 business days`}
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-[#1F2A44]/10 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 text-xs">
                    <button
                      onClick={() => downloadReceipt(ord._id)}
                      className="text-[#1F2A44] hover:text-[#E8B530] font-semibold inline-flex items-center gap-1"
                    >
                      <Download size={14} /> Receipt PDF
                    </button>
                  </div>

                  <Link
                    to={`/orders/${ord._id}`}
                    className="bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] text-xs font-semibold px-4 py-2 rounded-xl inline-flex items-center gap-1 transition"
                  >
                    Track & Order Details <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
    </UserPanelShell>
  );
};

export default OrdersPage;
